import { useState, useCallback } from 'react';
import { AdvancedFilterConfig, FilterOperator } from '../types/filter';
import { FilterConfig } from '@/shared/types/table';

interface UseTableFiltersProps {
  initialFilters?: FilterConfig[];
  onFiltersChange?: (filters: FilterConfig[]) => void;
}

/**
 * Convierte AdvancedFilterConfig a FilterConfig
 */
const toFilterConfig = (filter: AdvancedFilterConfig): FilterConfig => ({
  columnId: filter.columnId,
  operator: filter.operator,
  value: filter.value,
  value2: filter.value2,
});

export const useTableFilters = ({
  initialFilters = [],
  onFiltersChange,
}: UseTableFiltersProps = {}) => {
  const [filters, setFilters] = useState<AdvancedFilterConfig[]>(() =>
    initialFilters.map((filter, index) => ({
      id: `filter-${index}`,
      columnId: filter.columnId,
      columnLabel: filter.columnId,
      operator: filter.operator as FilterOperator,
      value: filter.value,
      value2: filter.value2,
    })),
  );

  /**
   * Agrega un nuevo filtro
   */
  const addFilter = useCallback(
    (filter: Omit<AdvancedFilterConfig, 'id'>) => {
      const newFilter: AdvancedFilterConfig = {
        ...filter,
        id: `filter-${Date.now()}-${Math.random()}`,
      };

      setFilters((prev) => {
        const updated = [...prev, newFilter];
        onFiltersChange?.(updated.map(toFilterConfig));
        return updated;
      });
    },
    [onFiltersChange],
  );

  /**
   * Actualiza un filtro existente
   */
  const updateFilter = useCallback(
    (filterId: string, updates: Partial<AdvancedFilterConfig>) => {
      setFilters((prev) => {
        const updated = prev.map((filter) =>
          filter.id === filterId ? { ...filter, ...updates } : filter,
        );
        onFiltersChange?.(updated.map(toFilterConfig));
        return updated;
      });
    },
    [onFiltersChange],
  );

  /**
   * Elimina un filtro por ID
   */
  const removeFilter = useCallback(
    (filterId: string) => {
      setFilters((prev) => {
        const updated = prev.filter((filter) => filter.id !== filterId);
        onFiltersChange?.(updated.map(toFilterConfig));
        return updated;
      });
    },
    [onFiltersChange],
  );

  /**
   * Limpia todos los filtros
   */
  const clearFilters = useCallback(() => {
    setFilters([]);
    onFiltersChange?.([]);
  }, [onFiltersChange]);

  /**
   * Establece todos los filtros (usado al aplicar cambios desde el popup)
   */
  const setAllFilters = useCallback(
    (newFilters: AdvancedFilterConfig[]) => {
      setFilters(newFilters);
      onFiltersChange?.(newFilters.map(toFilterConfig));
    },
    [onFiltersChange],
  );

  return {
    filters,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    setAllFilters,
  };
};
